import { Api } from '@monorepo/shared/apiClient';
import {
  DocumentFoldersCacheEvents,
  documentFoldersCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/documentFoldersCacheRegister';
import {
  OrganizationDocumentsCacheEvents,
  organizationDocumentsCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/organizationDocumentsCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { DocumentFileResponse } from 'mapistry-shared';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type UseOrganizationDocumentFilesListParams =
  Partial<Api.FetchAllOrganizationDocumentsParams> & {
    config?: QueryConfig<DocumentFileResponse[], Api.ErrorResponse>;
  };

const KEY_START = 'organization-document-files-list';

export const createKey = ({
  organizationId,
}: Api.FetchAllOrganizationDocumentsParams) =>
  [KEY_START, organizationId] as const;

const fetcher: Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchAllOrganizationDocuments
> = (_key, organizationId) =>
  Api.fetchAllOrganizationDocuments({ organizationId });

/** Every organization-scoped file, folder contents included. */
export function useOrganizationDocumentFilesList({
  organizationId,
  config: inputConfig,
}: UseOrganizationDocumentFilesListParams) {
  const isEnabled = !!organizationId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled ? createKey({ organizationId }) : undefined;
  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return { ...queryInfo, documentFiles: data };
}

type InvalidateCacheParams = {
  organizationId: string;
};

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([KEY_START, keyParams.organizationId]),
  );
}

organizationDocumentsCacheRegister(
  [
    OrganizationDocumentsCacheEvents.CREATE,
    OrganizationDocumentsCacheEvents.UPDATE,
    OrganizationDocumentsCacheEvents.DELETE,
    OrganizationDocumentsCacheEvents.MOVE,
  ],
  {
    hookName: 'useOrganizationDocumentFilesList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);

// Deleting a folder re-targets the files it held.
documentFoldersCacheRegister([DocumentFoldersCacheEvents.DELETE], {
  hookName: 'useOrganizationDocumentFilesList',
  callback: (keyParams) => invalidateCache(keyParams),
});
